import { GraduationCap, HeartHandshake, Sparkles } from "lucide-react";
import { Eyebrow, PageHero } from "@/components/site/bits";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/data/site";

const pillars = [
    { icon: GraduationCap, title: "Education First", text: "Clear, honest teaching about how addiction works — in the brain, in behavior, and in the heart." },
    { icon: HeartHandshake, title: "Person-to-Person", text: "Real counseling and guidance, one human being walking alongside another." },
    { icon: Sparkles, title: "Faith & Hope", text: "A spiritual foundation that restores dignity, purpose, and the belief that change is possible." },
];

export default function About() {
    return (
        <main>
            <PageHero
                testid="about-hero"
                eyebrow="About"
                title="Meet Dr. Sergio Jaquez"
                sub="Education. Counseling. Support. A path forward for individuals and families facing addiction."
                image={IMAGES.framework}
            />
            <section className="bg-white">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 sm:py-24 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative">
                            <div className="absolute -inset-3 rounded-2xl border border-[#C9A227]/50" />
                            <img
                                src={IMAGES.portrait}
                                alt="Dr. Sergio Jaquez"
                                data-testid="about-portrait"
                                className="relative w-full rounded-xl object-cover shadow-[0_35px_70px_-25px_rgba(8,22,42,0.6)]"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <Eyebrow>Compassion. Understanding. A Path Forward.</Eyebrow>
                        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0B1F3A]">
                            You Don’t Have to Navigate This Alone
                        </h2>
                        <div className="mt-5 space-y-4 text-base leading-relaxed text-[#4A5568]">
                            <p>
                                Dr. Sergio Jaquez provides education, personal guidance, and recovery-focused support
                                designed to help individuals better understand addiction, recognize behavioral
                                patterns, and develop tools for positive change.
                            </p>
                            <p>
                                Based in San Francisco, California, his work brings together clinical insight,
                                neuroscience education, and spiritual care — the Neuro-Bio-Spiritual approach at the
                                heart of the NCS Framework and his book, <em>Breaking the Chains</em>.
                            </p>
                        </div>
                        <blockquote className="mt-8 border-l-2 border-[#C9A227] pl-6">
                            <p className="text-lg font-medium italic leading-relaxed text-[#0B1F3A]">
                                “You don’t have to navigate this alone. There is hope, and there is a way forward.”
                            </p>
                            <footer className="mt-3 text-sm font-semibold text-[#4A5568]">— Dr. Sergio Jaquez</footer>
                        </blockquote>
                    </Reveal>
                </div>
            </section>
            <section className="bg-[#F9F8F3]">
                <div className="mx-auto max-w-7xl px-6 py-20">
                    <Reveal>
                        <Eyebrow>How He Works</Eyebrow>
                        <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B1F3A]">
                            Three Commitments to Every Person
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-8 md:grid-cols-3">
                        {pillars.map(({ icon: Icon, title, text }, i) => (
                            <Reveal key={title} delay={i * 0.08}>
                                <div data-testid={`about-pillar-${i}`} className="h-full rounded-xl border border-[#0B1F3A]/10 bg-white p-7 transition-transform duration-300 hover:-translate-y-1.5">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0B1F3A] text-[#D4AF37]">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="mt-4 text-lg font-bold text-[#0B1F3A]">{title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#4A5568]">{text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
